import { z } from 'zod';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA, FREIGHT_TYPE_SCHEMA, PRODUCT_TYPE_SCHEMA } from '@schuettflix/interfaces';
import { PriceSchema, PriceSchemaRules } from '@/shared/schemas/PriceSchema.ts';
import { LocationConfigurationSchema } from '@/modules/location/domain/LocationConfigurationSchema.ts';
import { LocationTypeSchema } from '@/modules/location/domain/LocationSchema.ts';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';

export type ProductTypeUnionParams = {
    errorMessages?: {
        required?: string;
    };
    formRules?: {
        priceRules?: PriceSchemaRules;
    };
};

export function getProductTypeUnion(params?: ProductTypeUnionParams) {
    const { errorMessages = {}, formRules = {} } = params ?? {};
    const { required } = errorMessages;
    const { priceRules } = formRules;

    return z.discriminatedUnion('productType', [
        // Non-transport products (including waste): material fields required.
        z.object({
            productType: PRODUCT_TYPE_SCHEMA.exclude([
                'TRANSPORT',
                'SERVICE_TOLL',
                'SERVICE_WEIGHING_NOTE',
                'SERVICE_ACCOMPANYING_DOCUMENT',
                'SERVICE_ENERGY_SURCHARGE',
            ]),
            salesMaterialQuantity: z.number({ required_error: required }).positive(required),
            salesMaterialUnit: z.enum(AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options, { required_error: required }),
            salesMaterialPrice: PriceSchema({
                ...priceRules,
                errorMessages: { required, nonNegative: required, multipleOf: required },
            }),
            salesMaterialTaxClassId: z.string({ message: required }),
            showZeroSalesMaterialPrice: z.boolean(),
            supplierOrganizationId: z
                .number({ message: required })
                .refine(id => !EXCLUDED_PARTNER_ORGANIZATION_IDS.includes(id), { message: required }),
            purchaseMaterialQuantity: z.number({ required_error: required }).positive(required),
            purchaseMaterialUnit: z.enum(AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options, { required_error: required }),
            purchaseMaterialPrice: PriceSchema({
                ...priceRules,
                errorMessages: { required, nonNegative: required, multipleOf: required },
            }),
            purchaseMaterialTaxClassId: z.string({ message: required }),
            showZeroPurchaseMaterialPrice: z.boolean(),
        }),
        // Transport-only: freightType + locations required, no material fields.
        z.object({
            productType: PRODUCT_TYPE_SCHEMA.extract(['TRANSPORT']),
            freightType: FREIGHT_TYPE_SCHEMA,
            loadingLocation: z.object(LocationConfigurationSchema.shape, { message: required }),
            unloadingLocation: z.object(LocationConfigurationSchema.shape, { message: required }),
            weighingLocation: z.enum(LocationTypeSchema.options, { message: required }).nullable(),
        }),
        z.object({
            productType: PRODUCT_TYPE_SCHEMA.extract([
                'SERVICE_TOLL',
                'SERVICE_WEIGHING_NOTE',
                'SERVICE_ACCOMPANYING_DOCUMENT',
                'SERVICE_ENERGY_SURCHARGE',
            ]),
        }),
    ]);
}
